/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2016-2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.contentEditorModule')
  .controller('ContentEditorController', [
    '$',
    '$compile',
    '$log',
    '$q',
    '$rootScope',
    '$scope',
    '$translate',
    '$window',
    'MediumEditor',
    'Restangular',
    'BulbAuthService',
    'BulbBrowserService',
    'BulbBusyService',
    'BulbCommentService',
    'BulbContentEditorService',
    'BulbMediumInsertBlockExtensionFactory',
    'BulbModalService',
    'BulbPageService',
    'BulbPasteExtensionFactory',
    'BulbToolbarExtensionFactory',
    'SerialSaveService',
    'UnitStateStore',
    'CKE_STATUS',
    'CONTENT_EDITOR',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    ContentEditorController]);

/**
 *
 * FUTURE : Broadcast events are currently used to inform descendant directives when toolbar buttons are selected.
 * This isn't necessarily the best way of handling it as it can be hard to debug and test.  However, it was
 * selected in light of being fast and easy to implement.  Something more along the lines of a tighter implementation
 * of an observer pattern might be better long term.
 *
 * NOTE : It is considered poor practice to refer or manipulate DOM elements from within an angular controller.  The
 * preferred separation of responsibilities is for controllers to interact with the model, and directives should do
 * any and all DOM interactions.  However, this situation is an exception.  In particular, since this is a content
 * editor which allows the user to actually edit page content, and thus make changes to elements.
 */
function ContentEditorController(
  $,
  $compile,
  $log,
  $q,
  $rootScope,
  $scope,
  $translate,
  $window,
  MediumEditor,
  Restangular,
  BulbAuthService,
  BulbBrowserService,
  BulbBusyService,
  BulbCommentService,
  BulbContentEditorService,
  BulbMediumInsertBlockExtensionFactory,
  BulbModalService,
  BulbPageService,
  BulbPasteExtensionFactory,
  BulbToolbarExtensionFactory,
  SerialSaveService,
  UnitStateStore,
  CKE_STATUS,
  CONTENT_EDITOR,
) {
  const that = this;
  const { currentUser } = BulbAuthService;
  const commentable = currentUser.isAuthenticated() && currentUser.features.CommentingFeature.enabled
    && BulbPageService.page.unit.commentsEnabled;

  // For storing current H2 status in selected text.
  this.isH2Selected = false;

  // For storing current H3 status in selected text.
  this.isH3Selected = false;

  // For storing current BOLD status in selected text.
  this.isBoldSelected = false;

  function unwrap($elements) {
    $elements.each(() => {
      const $helper = $('<b>helper</b>');
      const $element = $(this);
      $element.before($helper);
      $helper.after($element.contents());
      $helper.remove();
      $element.remove();
    });
  }

  this.page = BulbPageService.page;
  this.isToolbarStateText = false;

  // TODO CLEAN : Should these buttons be defined here, or by the directives that handle them?  If by the directives
  // there is a tighter coupling between the directives and the editor implementation, but if here, then there is
  // a tighter coupling between this controller and all the directives.
  const AlignLeftButton = MediumEditor.extensions.button.extend({
    name: 'alignLeft',
    contentDefault: '<b translate>toolbar_align_left_content</b>',
    contentFA: '<i class="fa fa-align-left"></i>',
    aria: $translate.instant('toolbar_align_left_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-align', 'left', that.selectedBlock);

      // TODO CLEAN : do this here, or rely on the handler of the event to do it if necessary?
      that.updateToolbar();
      that.editor.trigger('editableInput', null, that.editor.elements[0]);
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('align-left');
    },
  });

  const H2Button = MediumEditor.extensions.button.extend({
    name: 'h2',
    aria: $translate.instant('toolbar_h2_rollover'),
    contentDefault: '<b>H2</b>',
    contentFA: '<i class="far fa-h2"></i>',
    handleClick: function handleClick(event) {
      event.preventDefault();
      event.stopPropagation();

      that.isH2Selected = !that.isH2Selected;
      that.isBoldSelected = that.isH2Selected;

      // If H3 is selected and user click H2, unselect H3
      if (that.isH3Selected) {
        that.isH3Selected = false;
        this.execAction('append-h3');
      }
      // Execute the H2 action.
      this.execAction('append-h2');
      that.updateToolbar();
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      that.isH2Selected = (node.nodeName === 'H2' || node.parentElement.nodeName === 'H2');
      return that.isH2Selected;
    },
  });

  const H3Button = MediumEditor.extensions.button.extend({
    name: 'h3',
    aria: $translate.instant('toolbar_h3_rollover'),
    contentDefault: '<b>H3</b>',
    contentFA: '<i class="far fa-h3"></i>',
    handleClick: function handleClick(event) {
      event.preventDefault();
      event.stopPropagation();

      that.isH3Selected = !that.isH3Selected;
      that.isBoldSelected = that.isH3Selected;

      // If H2 is selected and user click H3, unselect H2
      if (that.isH2Selected) {
        that.isH2Selected = false;
        this.execAction('append-h2');
      }
      // Execute the H3 action.
      this.execAction('append-h3');
      that.updateToolbar();
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      that.isH3Selected = (node.nodeName === 'H3' || node.parentElement.nodeName === 'H3');
      return that.isH3Selected;
    },
  });

  const BoldButton = MediumEditor.extensions.button.extend({
    name: 'bold',
    aria: $translate.instant('toolbar_bold_rollover'),
    contentDefault: '<b>B</b>',
    contentFA: '<i class="fa fa-bold"></i>',
    tagNames: ['b', 'strong'],
    handleClick: function handleClick(event) {
      event.preventDefault();
      event.stopPropagation();

      that.isBoldSelected = !that.isBoldSelected;

      // If H2/H3 is selected and user click the bold, we remove the h2/h3 formatting as well.
      if (that.isH2Selected) {
        that.isH2Selected = false;
        that.isBoldSelected = false;
        this.execAction('append-h2');
        this.execAction('bold');
      } if (that.isH3Selected) {
        that.isBoldSelected = false;
        that.isH3Selected = false;
        this.execAction('append-h3');
      } else {
        // Execute the bold action.
        this.execAction('bold');
      }

      that.updateToolbar();
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      that.isBoldSelected = (node.nodeName === 'B' || node.parentElement.nodeName === 'B')
        || (['I', 'U', 'SPAN'].indexOf(node.nodeName) >= 0 && node.style.fontWeight === 'bold');
      if (!that.isBoldSelected && (that.isH2Selected || that.isH3Selected)) {
        that.isBoldSelected = true;
      }
      return that.isBoldSelected;
    },
  });

  const AlignCenterButton = MediumEditor.extensions.button.extend({
    name: 'alignCenter',
    contentDefault: '<b translate>toolbar_align_center_content</b>',
    contentFA: '<i class="fa fa-align-center"></i>',
    aria: $translate.instant('toolbar_align_center_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-align', 'center', that.selectedBlock);

      // TODO CLEAN : do this here, or rely on the handler of the event to do it if necessary?
      that.updateToolbar();
      that.editor.trigger('editableInput', null, that.editor.elements[0]);
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('align-center');
    },
  });

  const AlignRightButton = MediumEditor.extensions.button.extend({
    name: 'alignRight',
    contentDefault: '<b translate>toolbar_align_right_content</b>',
    contentFA: '<i class="fa fa-align-right"></i>',
    aria: $translate.instant('toolbar_align_right_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-align', 'right', that.selectedBlock);

      // TODO CLEAN : do this here, or rely on the handler of the event to do it if necessary?
      that.updateToolbar();
      that.editor.trigger('editableInput', null, that.editor.elements[0]);
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('align-right');
    },
  });

  const UnorderedListButton = MediumEditor.extensions.button.extend({
    name: 'myUnorderedList',
    contentDefault: '<b translate>toolbar_unordered_list_content</b>',
    contentFA: '<i class="fa fa-list-ul"></i>',
    aria: $translate.instant('toolbar_unordered_list_rollover'),
    tagNames: ['ul'],
    handleClick: function handleClick() {
      // Store the selection state before we perform the action, right when we click.
      this.selectionState = this.base.exportSelection();

      // This will handle the creation of the lists, and the styling and things that are needed.
      handleStylingOfLists('ul', this.document);

      // Execute the action, which is to either create or destroy the ordered list tags.
      const $editorElement = $(that.editorElement);
      const addElementTag = this.base.queryCommandState('insertunorderedlist');

      if (!addElementTag) {
        // This code is for REMOVING an ul, it: removes br tags, wraps the spans in "p" tags, and strips them.
        $editorElement.find('> span:not(.handled-span)').wrap('<p></p>');
        unwrap($editorElement.find('> p span:not(.handled-span)'));
      } else {
        // This code is for CREATING an ul, it: removes br tags, gets rid of wrapping p, and strips span tags.
        $editorElement.find('p > ul br').remove();
        $editorElement.find('p > ul').unwrap();
        unwrap($editorElement.find('ul span:not(.handled-span)'));
      }

      // After we are done clicking, we put the selection back.
      this.base.importSelection(this.selectionState);

      that.editor.trigger('editableInput', null, that.editor.elements[0]);
      that.updateToolbar();
    },
  });

  const OrderedListButton = MediumEditor.extensions.button.extend({
    name: 'myOrderedList',
    contentDefault: '<b translate>toolbar_ordered_list_content</b>',
    contentFA: '<i class="fa fa-list-ol"></i>',
    aria: $translate.instant('toolbar_ordered_list_rollover'),
    tagNames: ['ol'],
    handleClick: function handleClick(node) {
      // Store the selection state before we perform the action, right when we click.
      this.selectionState = this.base.exportSelection();

      // This will handle the creation of the lists, and the styling and things that are needed.
      handleStylingOfLists('ol', this.document);

      const $editorElement = $(that.editorElement);
      const addElementTag = this.base.queryCommandState('insertorderedlist');

      if (!addElementTag) {
        // This code is for REMOVING an ol, it: removes br tags, wraps the spans in "p" tags, and strips them.
        $editorElement.find('> span:not(.handled-span)').wrap('<p></p>');
        unwrap($editorElement.find('> p span:not(.handled-span)'));
      } else {
        // This code is for CREATING an ol, it: removes br tags, gets rid of wrapping p, and strips span tags.
        $editorElement.find('p > ol br').remove();
        $editorElement.find('p > ol').unwrap();
        unwrap($editorElement.find('ol span:not(.handled-span)'));
      }

      // After we are done clicking, we put the selection back.
      this.base.importSelection(this.selectionState);

      that.editor.trigger('editableInput', null, that.editor.elements[0]);
      that.updateToolbar();
    },
  });

  function handleStylingOfLists(listType, document) {
    // The "parentElement" is the highest tag/element in the hierarchy that the selection contains, in this
    // circumstance, it would probably be a "p" tag or something similar that can be transformed into a list.
    let parentElement = $(MediumEditor.selection.getSelectedParentElement(window.getSelection().getRangeAt(0)))
      .parent();

    // Determine what the opposing list type is here.
    const opposingListType = (listType === 'ol' ? 'ul' : 'ol');
    const isParentNotListType = ![listType, opposingListType].includes(parentElement.prop('tagName').toLowerCase());

    // This is required because if you have styling, the styling tag is added which adds an extra layer to the
    // parent element, making the function match with the styling tag such as <b>, etc. instead of the "ol" or "ul"
    // above.
    if (isParentNotListType) {
      for (const parent of $(parentElement).parents()) {
        if (parent.tagName.toLowerCase() === listType || parent.tagName.toLowerCase() === opposingListType) {
          parentElement = $(parent);
          break;
        }
      }
    }

    // If the blockContainer is already the current type of list, then revert it to a p tag.
    if (parentElement.prop('tagName').toLowerCase() === listType) {
      changeListTopLevelBack(parentElement);
    } else if (parentElement.prop('tagName').toLowerCase() === opposingListType) {
      // We can change the individual list tags here, instead of changing the entire list.
      changeIndividualListTags(listType, parentElement);
    } else {
      document.execCommand('formatBlock', false, 'pre');
      formatListFromPre(`<${listType}>`, `</${listType}>`);
    }
  }

  function changeListTopLevelBack(topElement, /*listType*/) {
    const $topElement = $(topElement);
    const listItemElements = $topElement.find('li');
    const orderedListElements = $topElement.find('ol');
    const unorderedListElements = $topElement.find('ul');

    // We first get rid of all the "li" elements, by replacing the "li" in them with <p> tags.
    for (let i = 0; i < listItemElements.length; i += 1) {
      const currentListItem = $($(listItemElements[i])[0]);
      currentListItem.replaceWith(`<p>${$(currentListItem).html()}</p>`);
    }

    // Then we need to find all the "ol" tags, and unwrap the contents of them, to make sure the list
    // structure looks appropriate.
    for (let j = 0; j < orderedListElements.length; j += 1) {
      const currentOrderedList = $($(orderedListElements[j])[0]);
      currentOrderedList.contents().unwrap();
    }

    // Then we need to find all the "ul" tags, and unwrap the contents of them, to make sure the list
    // structure looks appropriate.
    for (let k = 0; k < unorderedListElements.length; k += 1) {
      const currentUnorderedList = $($(unorderedListElements[k])[0]);
      currentUnorderedList.contents().unwrap();
    }

    $topElement.contents().unwrap();
  }

  function formatListFromPre(openingListTag, closingListTag) {
    const theList = $('pre')[0];
    const theElementsOfTheList = theList.childNodes;

    for (let i = 0; i < theElementsOfTheList.length; i += 1) {
      const current = $(theElementsOfTheList[i]);
      if (current.is('br')) {
        current.remove();
        i -= 1;
      } else {
        current.wrap('<li></li>');
      }
    }

    $(theList).replaceWith(openingListTag + $(theList).html() + closingListTag);
  }

  function changeIndividualListTags(newTag, parentElement) {
    const oldTag = (newTag === 'ol') ? 'ul' : 'ol';
    const firstRun = parentElement.html().replace(new RegExp(`<${oldTag}>`, 'g'), `<${newTag}>`);
    const finalRun = firstRun.replace(new RegExp(`</${oldTag}>`, 'g'), `</${newTag}>`);
    $(parentElement).replaceWith(`<${newTag}>${finalRun}</${newTag}>`);
  }

  const CropButton = MediumEditor.extensions.button.extend({
    name: 'crop',
    contentDefault: '<b translate>toolbar_crop_content</b>',
    contentFA: '<i class="fas fa-crop"></i>',
    aria: $translate.instant('toolbar_crop_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-crop', that.selectedBlock);
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('bulb-cropping');
    },
  });

  const RotateButton = MediumEditor.extensions.button.extend({
    name: 'rotate',
    contentDefault: '<b translate>toolbar_rotate_content</b>',
    contentFA: '<i class="fas fa-redo-alt"></i>',
    aria: $translate.instant('toolbar_rotate_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-rotate', that.selectedBlock);
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('bulb-rotating');
    },
  });

  const UpdateVideoThumbnail = MediumEditor.extensions.button.extend({
    name: 'updateVideo',
    contentDefault: '<b translate>toolbar_vide_thumbnail_content</b>',
    contentFA: '<i class="fas fa-edit"></i>',
    aria: $translate.instant('toolbar_video_thumbnail_rollover'),
    handleClick: function handleClick() {
      if (that.selectedBlock.video.splashImages.length <= 1) {
        const modalOptions = {
          titleCode: 'assetlibrary_modal_video_thumbnail_select',
          bodyCode: 'assetlibrary_modal_video_thumbnail_unavailable',
          showOkButton: true,
          showCancelButton: false,
        };

        BulbModalService.show(modalOptions);
      } else {
        Restangular.one('video').customGET(that.selectedBlock.video.id).then(
          (response) => {
            const modalOptions = {
              titleCode: '',
              controller: function controller() {
                this.splashImages = that.selectedBlock.video.splashImages;
                this.selectedId = response.splashImage.id;
                this.currentTarget = response;
              },
              templateUrl: 'splashimages.modal.nested',
              controllerAs: 'splashImagesModalCtrl',
              showOkButton: false,
              showCancelButton: false,
            };

            const videoId = that.selectedBlock.video.id;
            BulbModalService.show(modalOptions).then((selectedId) => {
              /*
               Preform an initial check here, so we don't have to send anything to the server if the
               newly selected splash image is the same image that the video already has.
               */
              if (response.selectedSplashImageId !== selectedId) {
                BulbBusyService.showBusyDuring(Restangular.one('video', videoId)
                  .patch({ selectedSplashImageId: response.selectedSplashImageId }))
                  .then((videoEntry) => {
                    const unitContentBlocks = that.page.unit.contentBlocks;
                    for (const blockId in unitContentBlocks) {
                      if (unitContentBlocks.hasOwnProperty(blockId)) {
                        if (unitContentBlocks[blockId].blockType === 'VIDEO'
                          && unitContentBlocks[blockId].video.id === videoEntry.id) {
                          angular.extend(unitContentBlocks[blockId].video, videoEntry);
                        }
                      }
                    }
                  });
              }
            });
          },
        );
      }
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('bulb-video-update');
    },
  });

  const ImageCarouselButton = MediumEditor.extensions.button.extend({
    name: 'carousel',
    contentDefault: '<b translate>toolbar_image_carousel_content</b>',
    contentFA: '<i class="far fa-images"></i>',
    aria: $translate.instant('toolbar_image_carousel_rollover'),
    handleClick: function handleClick() {
      // Retain reference to the original image-block that we are converting
      const originalImageBlock = that.selectedBlock;
      const convertImageCarouselDTO = {
        originalImageBlockId: originalImageBlock.id,
        deleteOriginal: 'true',
      };

      // Create the image-carousel block
      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(convertImageCarouselDTO, CONTENT_EDITOR.BLOCK_PATHS.IMAGE_CAROUSEL)
          .then((response) => {
            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // update content with new content block information
            // find placeholder, and replace with content block:
            const $contentBlockElement = jQuery(`#${originalImageBlock.id}`);

            // Replacing the current content block element, which is an image block, with the newly created
            // image carousel block.
            $contentBlockElement.replaceWith($compile(`<content-block id="${response.contentBlock.id
            }" contenteditable="false" class="align-center"></content-block>`)($scope));
            // TODO CLEAN - use a template rather than embedded string.

            // Emit an 'editableInput' event so that the image carousel block is saved to the page
            that.editor.trigger('editableInput', null, that.editor.elements[0]);

            // Update the toolbar to correctly display the new image carousel block:
            that.updateToolbar();

            // Remove the original image block. (the server call has already handled it on the server side)
            delete that.page.unit.contentBlocks[originalImageBlock.id];

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);
          }),
      });
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('bulb-image-carousel');
    },
  });

  const AddSlide = MediumEditor.extensions.button.extend({
    name: 'addSlide',
    contentDefault: '<b translate>toolbar_image_carousel_add_image_content</b>',
    contentFA: '<i class="fa fa-plus"></i>',
    aria: $translate.instant('toolbar_image_carousel_add_image_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-add-carousel-slide', that.selectedBlock);
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('bulb-add-carousel-slide');
    },
  });

  const RemoveSlide = MediumEditor.extensions.button.extend({
    name: 'removeSlide',
    contentDefault: '<b translate>toolbar_image_carousel_remove_image_content</b>',
    contentFA: '<i class="fa fa-minus"></i>',
    aria: $translate.instant('toolbar_image_carousel_remove_image_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-remove-carousel-slide', that.selectedBlock);
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('bulb-remove-carousel-slide');
    },
  });

  const RevertCarousel = MediumEditor.extensions.button.extend({
    name: 'revertCarousel',
    contentDefault: '<b translate>toolbar_image_carousel_revert_content</b>',
    contentFA: '<i class="fas fa-eraser"></i>',
    aria: $translate.instant('toolbar_image_carousel_revert_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-carousel-destroy', that.selectedBlock);
    },
  });

  const DeleteButton = MediumEditor.extensions.button.extend({
    name: 'delete',
    contentDefault: '<b>X</b>',
    contentFA: '<i class="fas fa-trash-alt"></i>',
    aria: $translate.instant('toolbar_delete_rollover'),
    handleClick: function handleClick() {
      // TODO CLEAN : verify a block is selected?

      // TODO UNDO : how do we undo/redo a delete?
      $scope.$broadcast('command-delete', that.selectedBlock);
    },
  });

  const EnableCarouselDraggable = MediumEditor.extensions.button.extend({
    name: 'dragCarousel',
    contentDefault: '<b translate>toolbar_image_carousel_draggable_content</b>',
    contentFA: '<i class="fas fa-arrows-alt"></i>',
    aria: $translate.instant('toolbar_image_carousel_draggable_content'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-carousel-draggable', {
        draggable: true,
        selectedBlock: that.selectedBlock,
        contentEditorCtrl: that,
      });
      const $repositionButton = jQuery('.medium-editor-action-dragCarousel');
      const repositionTitleTranslation = $translate.instant('toolbar_image_carousel_undo_draggable_content');
      $repositionButton.attr('title', repositionTitleTranslation);

      if ($repositionButton.hasClass('bulb-carousel-draggable-selected')) {
        $repositionButton.removeClass('bulb-carousel-draggable-selected');
      } else {
        $repositionButton.addClass('bulb-carousel-draggable-selected');
      }
    },
    isAlreadyApplied: function isAlreadyApplied(node) {
      return node.classList.contains('bulb-carousel-draggable-selected');
    },
  });

  const SaveButton = MediumEditor.extensions.button.extend({
    name: 'save',
    contentDefault: '<b translate>toolbar_save_content</b>',
    contentFA: '<i class="far fa-save"></i>',
    aria: $translate.instant('toolbar_save_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-save', that.selectedBlock);
    },
  });

  const CancelButton = MediumEditor.extensions.button.extend({
    name: 'cancel',
    contentDefault: '<b translate>toolbar_cancel_content</b>',
    contentFA: '<i class="fa fa-times"></i>',
    aria: $translate.instant('toolbar_cancel_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-cancel', that.selectedBlock);
    },
  });

  const CancelUploadButton = MediumEditor.extensions.button.extend({
    name: 'cancelUpload',
    contentDefault: '<b translate>toolbar_cancel_upload_content</b>',
    contentFA: '<i class="fa fa-times"></i>',
    aria: $translate.instant('toolbar_cancel_upload_rollover'),
    handleClick: function handleClick() {
      $scope.$broadcast('command-cancel-upload', that.selectedBlock);
    },
  });

  const CommentButton = MediumEditor.extensions.button.extend({
    name: 'comment',
    contentDefault: '<b translate>toolbar_comment_content</b>',
    contentFA: '<i class="fa fa-comment"></i>',
    aria: $translate.instant('toolbar_comment_rollover'),
    handleClick: function handleClick() {
      BulbCommentService.createCommentThread(that.page.unit, that.editor, that.selectedBlock);
      const toolbar = that.editor.getExtensionByName('toolbar');
      toolbar.hideToolbar();
    },
  });

  const TextButton = MediumEditor.extensions.button.extend({
    name: 'text',
    contentDefault: '<b>T</b>',
    aria: $translate.instant('toolbar_text_rollover'),
    currentSelection: null,
    handleClick: function handleClick() {
      // Change the toolbar to accommodate the larger button for the font,
      // and also toggle the menu opened/closed.
      this.currentSelection = that.editor.exportSelection();
      that.isToolbarStateText = !that.isToolbarStateText;

      const currentToolbar = $(this.base.getExtensionByName('toolbar').getToolbarElement());

      if (commentable) {
        // Find the toolbar that is being used in this click, and then alter the styles appropriately,
        // since there can be multiple toolbars, this is the safest resolution.
        if (that.isToolbarStateText
          && !currentToolbar.hasClass('medium-editor-text-options-styles')) {
          currentToolbar.toggleClass('medium-editor-text-options-styles');
        }
      } else {
        // Fixes the styles for the non-commentable version of the toolbar, same settings as above.
        if (that.isToolbarStateText
          && !currentToolbar.hasClass('medium-editor-text-options-styles-not-commentable')) {
          currentToolbar.toggleClass('medium-editor-text-options-styles-not-commentable');
        }

        // Since there is no comment button guaranteed here, we always toggle in a new class to change
        // the width to show it right in general.
        const $container = $('.medium-editor-list-container');
        if (!($container.hasClass('medium-editor-list-container-not-commentable'))) {
          $container.toggleClass('medium-editor-list-container-not-commentable');
        }
      }

      // Update the toolbar for the new selection, and make sure we get the "textEdit" state in the toolbar, or
      // removed from it.
      that.updateToolbar();
    },

    isAlreadyApplied: function isAlreadyApplied() {
      const current = JSON.stringify(this.currentSelection);
      const editorSelection = JSON.stringify(that.editor.exportSelection());
      // Check if we've changed the selection for the "textEdit" state which displays the text format options.
      // If we haven't changed the selection, then open the text format options, and adjust the styles.
      if (that.isToolbarStateText && current === editorSelection) {
        if (commentable) {
          $('div[id*="medium-editor-toolbar"]').addClass('medium-editor-text-options-styles');
        } else {
          $('div[id*="medium-editor-toolbar"]').addClass('medium-editor-text-options-styles-not-commentable');
        }

        return true;
      }

      // If we have changed the selection, disable the textEdit state and "unpress" the text format button.
      that.isToolbarStateText = false;
      return false;
    },
  });

  /**
   * Custom `color picker` extension
   */
  const ColorPickerButton = MediumEditor.extensions.button.extend({
    name: 'colorPicker',
    action: 'applyForeColor',
    aria: $translate.instant('toolbar_color_rollover'),
    contentDefault: '<span class="editor-color-picker"><i class="current-color-block fa fa-square fa-2x"></i><span>',
    shouldBeClosed: false,
    handleClick: function handleClick(e) {
      // Check if the color picker window is open, if it is, make sure it closes.
      if (this.shouldBeClosed) {
        return;
      }

      // Toggle the color picker window flag to present the window or not.
      this.shouldBeClosed = !this.shouldBeClosed;

      // Prevent the event default on the button click.
      e.preventDefault();
      e.stopPropagation();

      // If no text selected, stop here.
      if (this.selectionState && (this.selectionState.end - this.selectionState.start === 0)) {
        return;
      }

      // Colors for picker, specified in contenteditor.js in the CONSTANTS.
      const pickerColors = CONTENT_EDITOR.COLORS;

      // Create the picker and set it up appropriately.
      const picker = vanillaColorPicker(this.document // jshint ignore: line
        .querySelector('.medium-editor-toolbar-active .editor-color-picker'));
      picker.set('customColors', pickerColors);
      picker.set('positionOnTop');
      picker.openPicker();
      picker.on('colorChosen', (color) => {
        // If the color is not one of our chosen colors, then just close the picker.
        if (CONTENT_EDITOR.COLORS.includes(color)) {
          this.document.execCommand('styleWithCSS', false, true);
          this.document.execCommand('foreColor', false, color);

          // Find spans, add the "handled-span" class to the appropriate span, so it is not removed via
          // our multiple span clean-up options.
          const newSpanTags = $('.bulb-page-content span');
          for (let i = 0; i < newSpanTags.length; i += 1) {
            const currentTag = $(newSpanTags[i]);
            if (BulbContentEditorService.convertRGBtoHEX(currentTag.css('color')) === color
              && !currentTag.hasClass('handled-span')) {
              currentTag.addClass('handled-span');
            }
          }

          // Set the current color of the text, so that we can change the small color block.
          const currentColor = BulbContentEditorService
            .convertRGBtoHEX(`${this.document.queryCommandValue('ForeColor')}`);
          $('.current-color-block').css('color', currentColor);
        }
        // Closes the picker if the selection is manual entry (not in our approved font colors), or if we are
        // finished doing what we need to do for the font color.
        picker.emit('lostFocus');
      });
    },
    isAlreadyApplied: function isAlreadyApplied() {
      // Sets the current color of the box.
      const currentColor = BulbContentEditorService
        .convertRGBtoHEX(`${this.document.queryCommandValue('ForeColor')}`);
      $('.current-color-block').css('color', currentColor);

      return CONTENT_EDITOR.COLORS.includes(currentColor) && currentColor !== '#363d47';
    },
  });

  // TODO CLEAN : prefer not to deal with DOM elements in a controller, how do we handle that more cleanly?
  this.selectedBlockElement = null;
  this.selectedBlock = null;
  this.insertBlockExtension = new BulbMediumInsertBlockExtensionFactory.CreateExtension({
    blockSelected: function blockSelected(element) {
      that.selectedBlockElement = angular.element(element);
      that.selectedBlock = element ? that.page.unit.contentBlocks[element.id] : null;
      $scope.$broadcast('selectionChange', that.selectedBlockElement, that.selectedBlock);

      // Remove the styles for the text edit button in case we switch to a block that doesn't involve text.
      $('.medium-editor-text-options-styles').removeClass('medium-editor-text-options-styles');
      $('.medium-editor-text-options-styles-not-commentable')
        .removeClass('medium-editor-text-options-styles-not-commentable');

      that.updateToolbar();
      // TODO POLISH : any way to fix the delay when the selection changes?.
    },
    scope: $scope,
    contentEditorCtrl: that,
  });

  /*
   * @staticToolbar, @stickyToolbar
   * Were used in helping mobile support in medium-editor
   * until issue #1040 is resolved within the project,
   * assume these values to always be false.
   *
   * Issue URL: https://github.com/yabwe/medium-editor/issues/1040
   */
  const staticToolbar = false;
  const stickyToolbar = false;

  this.toolbarExtension = new BulbToolbarExtensionFactory.CreateExtension({
    buttons: [
      'dropdownExtension',
      'colorPicker',
      {
        name: 'h2',
        aria: $translate.instant('toolbar_h2_rollover'),
        contentFA: '<i class="far fa-h2"></i>',
      },
      {
        name: 'h3',
        aria: $translate.instant('toolbar_h3_rollover'),
        contentFA: '<i class="far fa-h3"></i>',
      },
      {
        name: 'bold',
        aria: $translate.instant('toolbar_bold_rollover'),
      },
      {
        name: 'italic',
        aria: $translate.instant('toolbar_italic_rollover'),
      },
      {
        name: 'underline',
        aria: $translate.instant('toolbar_underline_rollover'),
      },
      'text',
      {
        name: 'quote',
        aria: $translate.instant('toolbar_quote_rollover'),
      },
      'myUnorderedList',
      'myOrderedList',
      {
        name: 'anchor',
        aria: $translate.instant('toolbar_anchor_rollover'),
      },
      'alignLeft',
      'alignCenter',
      'alignRight',
      'updateVideo',
      'crop',
      'rotate',
      'carousel',
      'addSlide',
      'removeSlide',
      'revertCarousel',
      'dragCarousel',
      'delete',
      'save',
      'cancel',
      'cancelUpload',
      'comment',
    ],
    static: staticToolbar,
    standardizeSelectionStart: true,
    sticky: stickyToolbar,
  });

  const mediumEditorToolbarStates = new $window.MediumEditorToolbarStates({
    default: {
      buttons: ['bold', 'italic', 'underline', 'text', 'quote',
        'myUnorderedList', 'myOrderedList', 'anchor'],
    },
    textEdit: {
      buttons: ['dropdownExtension', 'colorPicker', 'h2', 'h3', 'bold', 'italic', 'underline', 'text', 'h2', 'h3',
        'quote', 'myUnorderedList', 'myOrderedList', 'anchor'],
    },
    image: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'crop', 'rotate', 'delete'],
    },
    imagePro: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'crop', 'rotate', 'carousel', 'delete'],
    },
    imageCarousel: {
      buttons: ['crop', 'rotate', 'addSlide', 'removeSlide', 'revertCarousel', 'dragCarousel', 'delete'],
    },
    embeddedVideo: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'delete'],
    },
    uploadedVideo: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'updateVideo', 'delete'],
    },
    uploadedAudio: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'delete'],
    },
    crop: {
      buttons: ['crop', 'save', 'cancel'],
    },
    caption: {
      buttons: ['bold', 'italic', 'underline', 'anchor'],
    },
    attachedFile: {
      buttons: ['delete'],
    },
    uploading: {
      buttons: ['cancelUpload'],
    },
    embed: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'delete'],
    },
    widgetEmbed: {
      buttons: ['delete'],
    },
  });

  const mediumEditorToolbarStatesCommentable = new $window.MediumEditorToolbarStates({
    default: {
      buttons: ['bold', 'italic', 'underline', 'text', 'quote',
        'myUnorderedList', 'myOrderedList', 'anchor', 'comment'],
    },
    textEdit: {
      buttons: ['dropdownExtension', 'colorPicker', 'h2', 'h3', 'bold', 'italic', 'underline', 'text', 'h2', 'h3',
        'quote', 'myUnorderedList', 'myOrderedList', 'anchor', 'comment'],
    },
    image: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'crop', 'rotate', 'delete', 'comment'],
    },
    imagePro: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'crop', 'rotate', 'carousel', 'delete', 'comment'],
    },
    imageCarousel: {
      buttons: ['crop', 'rotate', 'addSlide', 'removeSlide', 'revertCarousel', 'dragCarousel', 'delete', 'comment'],
    },
    embeddedVideo: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'delete', 'comment'],
    },
    uploadedVideo: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'updateVideo', 'delete', 'comment'],
    },
    uploadedAudio: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'delete', 'comment'],
    },
    crop: {
      buttons: ['crop', 'save', 'cancel'],
    },
    caption: {
      buttons: ['bold', 'italic', 'underline', 'anchor'],
    },
    attachedFile: {
      buttons: ['delete', 'comment'],
    },
    uploading: {
      buttons: ['cancelUpload'],
    },
    embed: {
      buttons: ['alignLeft', 'alignCenter', 'alignRight', 'delete', 'comment'],
    },
    widgetEmbed: {
      buttons: ['delete', 'comment'],
    },
  });

  this.editorOptions = {
    imageDragging: false,
    anchor: {
      linkValidation: true,
      placeholderText: $translate.instant('toolbar_anchor_placeholder'),
    },
    buttonLabels: 'fontawesome',
    placeholder: {
      // TODO POLISH : would be nice if this was false, but it stays visible too long when uploading
      // images and videos, so it doesn't look good, so it is set to true for now, until we have time to work
      // on making sure the placeholder is hidden sooner when uploading.
      hideOnClick: false,
    },
    extensions: {
      dropdownExtension: new MediumEditor.extensions.dropdown({
        execOn: 'fontName',
        defaultContentItems: CONTENT_EDITOR.FONTS,
        classList: ['medium-editor-action-fontname'],
        browserService: BulbBrowserService,
      }),
      bold: new BoldButton(),
      h2: new H2Button(),
      h3: new H3Button(),
      tabBehavior: new MediumEditor.extensions.tabBehavior(),
      alignLeft: new AlignLeftButton(),
      alignCenter: new AlignCenterButton(),
      alignRight: new AlignRightButton(),
      crop: new CropButton(),
      rotate: new RotateButton(),
      carousel: new ImageCarouselButton(),
      addSlide: new AddSlide(),
      removeSlide: new RemoveSlide(),
      revertCarousel: new RevertCarousel(),
      dragCarousel: new EnableCarouselDraggable(),
      // TODO INDENT
      // 'indent': new IndentButton(),
      // 'outdent': new OutdentButton(),
      updateVideo: new UpdateVideoThumbnail(),
      myUnorderedList: new UnorderedListButton(),
      myOrderedList: new OrderedListButton(),
      delete: new DeleteButton(),
      save: new SaveButton(),
      cancel: new CancelButton(),
      cancelUpload: new CancelUploadButton(),
      comment: new CommentButton(),
      text: new TextButton(),
      colorPicker: new ColorPickerButton(),
      bulbInsertBlock: this.insertBlockExtension,
      toolbar: this.toolbarExtension,
      paste: new BulbPasteExtensionFactory.CreateExtension({
        forcePlainText: false,
        cleanPastedHTML: true,
      }),
      // TODO CLEAN - convert this into a factory like above
      'medium-editor-toolbar-states': new $window.MediumEditorToolbarStates({
        states: commentable ? mediumEditorToolbarStatesCommentable : mediumEditorToolbarStates,
        getStateNameForNode: function getStateNameForNode(node) {
          if (['P', 'H2', 'H3', 'LI', 'UL', 'I', 'B', 'U', 'SPAN', 'BLOCKQUOTE'].indexOf(node.nodeName) >= 0) {
            const thisIsACaption = $(node).closest('.bulb-edit-caption').length > 0;
            if (that.isToolbarStateText && !thisIsACaption) {
              return 'textEdit';
            } if (thisIsACaption) {
              return 'caption';
            }

            return 'default';
          }
          return node.getAttribute(this.attributeName);
        },
        attributeName: 'toolbar-state',
      }),
    },
    keyboardCommands: true,
  };

  this.updateToolbar = function updateToolbar() {
    if (BulbPageService.page?.unit?.ckeStatus !== CKE_STATUS.UPGRADED) {
      that.editor.checkSelection();
    }
  };

  // Making sure we hide the content editor toolbar when navigating to a new state.
  const stateChangeDetection = $rootScope.$on('$stateChangeStart', (/*event, nextState, fromState*/) => {
    $window.getSelection().removeAllRanges();
    that.updateToolbar();
    // Unregisters the event listener that we just created.
    stateChangeDetection();
  });

  /*
   * This code is 99% from medium-editor.js there is just a slight modification where I removed a conditional checking
   * if selectionState.emptyBlockIndex !== undefined. This incorrectly behaves with our content-blocks which in turn
   * results in mis-selecting when attempting to link text below any content block. Modifications below are wrapped
   * in a 'bulb MOD' comment block.
   */
  MediumEditor.selection.importSelection = function mediumEditorImportSelection(
    selectionState,
    root,
    doc,
    favorLaterSelectionAnchor,
  ) {
    if (!selectionState || !root) {
      return;
    }

    let range = doc.createRange();
    range.setStart(root, 0);
    range.collapse(true);

    let node = root;
    const nodeStack = [];
    let charIndex = 0;
    let foundStart = false;
    let foundEnd = false;
    let trailingImageCount = 0;
    let stop = false;
    let nextCharIndex;
    let allowRangeToStartAtEndOfNode = false;
    let lastTextNode = null;

    if (favorLaterSelectionAnchor || selectionState.startsWithImage
      || typeof selectionState.emptyBlocksIndex !== 'undefined') {
      allowRangeToStartAtEndOfNode = true;
    }

    while (!stop && node) {
      // Only iterate over elements and text nodes
      if (node.nodeType > 3) {
        node = nodeStack.pop();
        continue;
      }

      // If we hit a text node, we need to add the amount of characters to the overall count
      if (node.nodeType === 3 && !foundEnd) {
        nextCharIndex = charIndex + node.length;

        // Check if we're at or beyond the start of the selection we're importing
        if (!foundStart && selectionState.start >= charIndex && selectionState.start <= nextCharIndex) {
          // NOTE: We only want to allow a selection to start at the END of an element if
          //  allowRangeToStartAtEndOfNode is true
          if (allowRangeToStartAtEndOfNode || selectionState.start < nextCharIndex) {
            range.setStart(node, selectionState.start - charIndex);
            foundStart = true;
          }
          // We're at the end of a text node where the selection could start, but we shouldn't
          // make the selection start here because allowRangeToStartAtEndOfNode is false.
          // However, we should keep a reference to this node in case there aren't any more
          // text nodes after this, so that we have somewhere to import the selection to
          else {
            lastTextNode = node;
          }
        }
        // We've found the start of the selection, check if we're at or beyond the end of the selection we're importing
        if (foundStart && selectionState.end >= charIndex && selectionState.end <= nextCharIndex) {
          if (!selectionState.trailingImageCount) {
            range.setEnd(node, selectionState.end - charIndex);
            stop = true;
          } else {
            foundEnd = true;
          }
        }
        charIndex = nextCharIndex;
      } else {
        if (selectionState.trailingImageCount && foundEnd) {
          if (node.nodeName.toLowerCase() === 'img') {
            trailingImageCount += 1;
          }
          if (trailingImageCount === selectionState.trailingImageCount) {
            // Find which index the image is in its parent's children
            let endIndex = 0;
            while (node.parentNode.childNodes[endIndex] !== node) {
              endIndex += 1;
            }
            range.setEnd(node.parentNode, endIndex + 1);
            stop = true;
          }
        }

        if (!stop && node.nodeType === 1) {
          // this is an element
          // add all its children to the stack
          let i = node.childNodes.length - 1;
          while (i >= 0) {
            nodeStack.push(node.childNodes[i]);
            i -= 1;
          }
        }
      }

      if (!stop) {
        node = nodeStack.pop();
      }
    }

    // If we've gone through the entire text but didn't find the beginning of a text node
    // to make the selection start at, we should fall back to starting the selection
    // at the END of the last text node we found
    if (!foundStart && lastTextNode) {
      range.setStart(lastTextNode, lastTextNode.length);
      range.setEnd(lastTextNode, lastTextNode.length);
    }

    /*
     * bulb MOD BEGIN
     *if (typeof selectionState.emptyBlocksIndex !== 'undefined') {
     *  range = this.importSelectionMoveCursorPastBlocks(doc, root, selectionState.emptyBlocksIndex, range);
     *}
     * bulb MOD END
     */

    // If the selection is right at the ending edge of a link, put it outside the anchor tag instead of inside.
    if (favorLaterSelectionAnchor) {
      range = MediumEditor.selection.importSelectionMoveCursorPastAnchor(selectionState, range);
    }

    const sel = doc.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };
}
